import React from 'react';
import HamQuest from '../api/HamQuestGiver';
import { QuestRoute } from '../api/QuestRoutes';
import Button from '../forms/Button';
import Col from '../layout/Col';
import Row from '../layout/Row';
import Select from '../forms/Select';
import Input from '../forms/Input';
import Checkbox from '../forms/Checkbox';
import { formatPhone, generateId, translate } from '../utility/functions';
import Table from '../table/Table';
import Form from '../forms/Form';
import { HSOptions } from '../../constants/HSOptions';
import ManagementRow from '../table/ManagementRow';
import ModalFactory from '../Modal';
import GetState from '../GetState';
import Loading from '../Loading';
import { SCREEN_WIDTH } from '../../constants/config';

export const showMediaId = (media = []) => {
    const types = {
        LP: 'LicensePlate',
        RF: 'RFID',
        App: 'AppId',
    };

    const mediaMap = new Map();
    media.forEach(e => mediaMap.set(e.mediaType, e));
    if (mediaMap.has(types.LP)) {
        return media.find(e => e.mediaType === types.LP && !e.backupPlate)?.mediaId ?? 'No Primary';
    }

    if (mediaMap.has(types.RF)) {
        return mediaMap.get(types.RF).mediaId;
    }

    return 'None on File';
}


const UserMedia = ({ userId, states, refreshState = () => { } }) => {
    const [user, refreshUser] = GetState(QuestRoute.user.info(userId))
    const id = generateId();

    const types = {
        LP: 'LicensePlate',
        RF: 'RFID',
        App: 'AppId',
    };

    const deleteMedia = async (media) => {
        
        let hasActivePlateReadAlert = false;
        if(media.mediaType === 'LicensePlate')
        {
           const flags = await HamQuest.get(QuestRoute.loyaltyMedia.flags(media.mediaId));
            hasActivePlateReadAlert = Array.isArray(flags) && flags.length > 0;         
        }

        const confirmMessage = hasActivePlateReadAlert
            ? (
                <>
                    <p>This plate has unresolved plate read alerts.</p>
                    <p>Deleting this media will automatically mark all outstanding alerts as resolved.</p>
                </>
            )
            : (
                <>Are you sure you want to delete this Media ID?</>
            );

        ModalFactory.confirmAndProcess({
            confirmation: {
                title: 'Are you sure?',
                body: confirmMessage,
                type: 'warning',
                slideToAccept: true,
            },
            promise: {
                title: 'Removing Media ID',
                callback: () => HamQuest.delete(QuestRoute.user.mediaID(userId, media.id))
            },
            finally: () => {
                refreshUser();
                refreshState();
            }
        });
    };

    const editPopup = (user, media) => {
        // console.log(user)
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Edit ${user.firstName}'s ${media.mediaType}`,
            body: (
                <Form
                    inputs={{
                        id: media.id,
                        mediaId: media.mediaId,
                        status: media.status,
                        mediaType: media.mediaType,
                        backupPlate: media.backupPlate,
                        state: media.state,
                    }}
                    onSubmit={e => (
                        HamQuest.put(QuestRoute.user.mediaID(user.customerId, media.id), { ...e })
                            .then(() => {
                                refreshUser();
                                refreshState();
                                ModalFactory.close(id);
                            }))}
                >
                    {({ values }) => (
                        <>
                            <Input
                                name='mediaId'
                                label={translate(media.mediaType)}
                                sanitize={e => {
                                    if (values.mediaType === types.LP) {
                                        return e
                                            .replace(/./g, (char) => char.toUpperCase())
                                            .replace(/[^a-z0-9]/gi, '');
                                    } else {
                                        return e.replace(/[^0-9]/gi, '');
                                    }
                                }}
                                maxLength={values.mediaType === types.LP ? 8 : 100}
                                readOnly={values.mediaType === types.RF}
                                required
                            />
                            <Select
                                name='status'
                                label='Status'
                                options={HSOptions.user.media.status}
                                required
                            />
                            {values.mediaType === types.LP &&
                                <Select
                                    label='State'
                                    name='state'
                                    options={states}
                                    required
                                />
                            }
                            {values.mediaType === types.LP &&
                                <Checkbox
                                    name='backupPlate'
                                    label='Backup Plate'
                                />
                            }
                        </>
                    )}
                </Form>
            )
        })
    }

    const newPopup = () => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: `New Media`,
            body: (
                <Form
                    inputs={{
                        mediaType: 'LicensePlate',
                        tagType: 'Hamilton',
                        status: 'Active',
                        mediaId: '',
                        state: '',
                        backupPlate: false
                    }}
                    onSubmit={e => (
                        HamQuest.post(QuestRoute.user.mediaIDs(userId), { ...e })
                            .then(() => {
                                refreshUser();
                                refreshState();
                                ModalFactory.close(id);
                            })
                    )}
                >
                    {({ values }) => (
                        <>
                            <Select
                                name='mediaType'
                                label='Media Type'
                                options={HSOptions.user.media.types}

                                required
                            />

                            {values.mediaType === types.RF &&
                                <Select
                                    name='tagType'
                                    label='Tag Type'
                                    options={HSOptions.user.media.tags}
                                    required
                                />
                            }

                            <Select
                                label='Status'
                                name='status'
                                options={HSOptions.user.media.status}
                                required
                            />

                            {values.mediaType === types.LP &&
                                <Select
                                    label='State'
                                    name='state'
                                    options={states}
                                    required
                                />
                            }

                            {values.mediaType === types.RF &&
                                <Input
                                    label='RFID'
                                    value={values.tagType === 'Hamilton' ? '1026...' : 'AAA...'}
                                    disabled
                                    required
                                />
                            }

                            <Input
                                name='mediaId'
                                label='Media ID'
                                sanitize={e => {
                                    if (values.mediaType === types.LP) {
                                        return e
                                            .replace(/./g, (char) => char.toUpperCase())
                                            .replace(/[^a-z0-9]/gi, '');
                                    } else {
                                        return e.replace(/[^0-9]/gi, '');
                                    }
                                }}
                                maxLength={values.mediaType === types.LP ? 8 : 100}
                                required
                            />

                            {values.mediaType === types.LP &&
                                <Checkbox
                                    name='backupPlate'
                                    label='Backup Plate'
                                />
                            }
                        </>
                    )}
                </Form>
            )
        })
    }

    const maskRFID = (id) => {
        const startingIndex = id[0] === '1' ? 4 : 3;
        const nonZeroIndex = id.slice(startingIndex).search(/[^0]/) + startingIndex;
        return `${id.slice(0, startingIndex)}...${id.slice(nonZeroIndex)}`;
    }

    if (!user) return <Loading />;

    const tableConfig = {
        data: user.loyaltyMedias?.filter(e => e.mediaType !== types.App),
        disableTableControls: true,
        disableHeaderSearch: true,
        disableAnimation: true,
        disableMobileView: true,
        headers: [
            {
                label: 'Type',
                propKey: 'mediaType',
            }, {
                label: 'Media ID',
                propKey: 'mediaId',
            }, {
                label: 'Plate Type',
                propKey: 'backupPlate',
            },
        ],
        renderFormat: (e) => (
            <ManagementRow
                key={generateId()}
                controls={[
                    <Button onClick={() => editPopup(user, e)} key={generateId()} >Edit</Button>,
                    <Button onClick={() => deleteMedia(e)} key={generateId()} >Delete</Button>
                ]}
            >
                <td>{e.mediaType}</td>
                <td>{e.mediaType === types.RF ? maskRFID(e.mediaId) : e.mediaId}</td>
                <td>{e.mediaType === types.LP ? e.backupPlate ? 'Backup' : <b>Primary</b> : 'NA'}</td>
            </ManagementRow>
        )
    }

    return (
        <Col style={SCREEN_WIDTH ? {} : { width: '30vw' }}>
            <Row><b>Name:</b><span>{user.fullName}</span></Row>
            <Row><b>Email:</b><span>{user.email}</span></Row>
            <Row><b>Phone:</b><span>{formatPhone(user.cellPhoneNumber) || '-'}</span></Row>
            {user.awardName && <Row><b>Club:</b><span>{user.awardName}</span></Row>}
            <Row>
                <Button onClick={() => newPopup(user)}>Add New Media</Button>
                <Button customerId={user.userId} onClick={() => ModalFactory.close(id)}>View User</Button>
            </Row>
            <Table config={tableConfig} />
        </Col>
    )
}

export default UserMedia;
